import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { State } from "country-state-city";
import ReactDOM from "react-dom";
import {
  ChevronRight,
  Camera,
  User,
  IdCard,
  Receipt,
  Bell,
  ClipboardCheck,
  Landmark,
  LifeBuoy,
  HelpCircle,
  ShieldCheck,
} from 'lucide-react';
import axios from "../../../axiosInstance.js";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BottomNavigationCustomer from "./BottomNavigatonCustomer";
import CurrentOrdersList from "../VendorResponsesCustomer/CurrentOrdersList";
import './UserProfileSection.css';

const UserProfileSection = () => {
  const [states, setStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); 
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // NEW
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName') || 'ClaimPro User';
  console.log('user', userId)

  useEffect(() => {
    loadStates();
  }, []);

  const loadStates = () => {
    setIsLoadingStates(true);
    try {
      const allStates = State.getStatesOfCountry("IN");
      setStates(allStates);
      setFilteredStates(allStates);
    } catch (error) {
      console.error("Error loading states:", error);
      setStates([]);
      setFilteredStates([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value.toLowerCase();
    setSearchTerm(val);
    setFilteredStates(
      states.filter((state) =>
        state.name.toLowerCase().includes(val)
      )
    );
  };

  const handleSelect = () => {
    if (selectedState) {
      alert(`You selected: ${selectedState.name}`);
    }
  };

  /* =========================================================
     AVATAR PHOTO SELECT / UPLOAD
     -----------------------------------------------------------
     Pulled from UserProfileDetails.jsx: a hidden file input
     triggered by the camera button, previewed via FileReader,
     then PUT to the same per-role update endpoint used there.
     Previously the camera button just called navigate(...), so
     it never actually opened the camera/photo picker.
  ========================================================= */
  const profileInputRef = useRef(null);
  const [profileFile, setProfileFile] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const handleAvatarClick = () => {
    if (profileInputRef.current) {
      profileInputRef.current.click();
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 9097152) {
      toast.error('File size should be less than 2 MB!');
      return;
    }

    // Preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileFile(reader.result);
    };
    reader.readAsDataURL(file);

    uploadProfilePhoto(file);
  };

  const uploadProfilePhoto = async (file) => {
    let urlPart = '';
    if (userId?.startsWith("CC-")) urlPart = 'customerUpdate';
    if (userId?.startsWith("CUD-")) urlPart = 'updateCustomerDriver';
    if (userId?.startsWith("VC-")) urlPart = 'venderUpdate';
    if (userId?.startsWith("VED-")) urlPart = 'updateDriverInfoVendor';

    if (!urlPart) return;

    const formDataObj = new FormData();
    formDataObj.append('profileImage', file, file.name);

    setIsUploadingPhoto(true);
    try {
      const response = await axios({
        method: 'PUT',
        url: `${process.env.REACT_APP_BACKEND_URL}/api/${urlPart}/${userId}/${userId}`,
        data: formDataObj,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.status === true) {
        toast.success('Profile photo updated!');
      } else {
        toast.error('Could not update photo. Please try again.');
      }
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      toast.error('Could not update photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

 const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.clear();
    sessionStorage.clear();
    navigate('/loginPage');
  };

  // NOTE: this should call your real delete-account API before navigating away —
  // wiring left as a placeholder since no such endpoint was in the original file.
  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };
  const confirmDeleteAccount = () => {
    setShowDeleteConfirm(false);
    navigate('/delete-account-page');
  };

  return (
    <div className="ups-page font-satoshi">
      <div className="ups-scroll">

        {/* ============ AVATAR + IDENTITY ============ */}
        <div className="ups-identity">
          <div className="ups-avatar-wrap">
            {profileFile ? (
              <img
                className="ups-avatar"
                style={{ objectFit: 'cover' }}
                src={profileFile}
                alt=""
                onClick={handleAvatarClick}
              />
            ) : (
              <div className="ups-avatar" onClick={handleAvatarClick}>
                {userName.charAt(0).toUpperCase()}
              </div>
            )}
            <input
              hidden
              ref={profileInputRef}
              type="file"
              name="profileImage"
              onChange={handleProfileImageChange}
              accept="image/*"
              className="form-control"
            />
            <button
              type="button"
              className="ups-avatar-edit"
              onClick={handleAvatarClick}
              disabled={isUploadingPhoto}
              aria-label="Change photo"
            >
              <Camera size={13} />
            </button>
          </div>

          <h2 className="ups-name">{userName}</h2>
          <span className="ups-enterprise-id">Enterprise ID: {userId || '—'}</span>
        </div>

        {/* ============ ACCOUNT DETAILS ============ */}
        <p className="ups-section-label">Account Details</p>
        <div className="ups-card">
          <div className="ups-row" onClick={() => navigate('/user-profile-details')}>
            <span className="ups-row-icon"><User size={16} /></span>
            <span className="ups-row-label">Personal Information</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/kyc-documents')}>
            <span className="ups-row-icon"><IdCard size={16} /></span>
            <span className="ups-row-label">My KYC Documents</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/invoices-for-customers')}>
            <span className="ups-row-icon"><Receipt size={16} /></span>
            <span className="ups-row-label">Transactions &amp; Invoices</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          {userId && userId.startsWith('VC-') && (
            <div className="ups-row" onClick={() => navigate('/TDS-Application-page')}>
              <span className="ups-row-icon"><Landmark size={16} /></span>
              <span className="ups-row-label">TDS</span>
              <ChevronRight size={16} className="ups-row-arrow" />
            </div>
          )}
        </div>

        {/* ============ ACTIVITY & REPORTS ============ */}
        <p className="ups-section-label">Activity &amp; Reports</p>
        <div className="ups-card">
          <div className="ups-row" onClick={() => navigate('/customer-activity')}>
            <span className="ups-row-icon"><Bell size={16} /></span>
            <span className="ups-row-label">Updates</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/inspection-reports')}>
            <span className="ups-row-icon"><ClipboardCheck size={16} /></span>
            <span className="ups-row-label">Inspection Reports</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
        </div>

        {/* ============ SUPPORT & LEGAL ============ */}
        <p className="ups-section-label">Support &amp; Legal</p>
        <div className="ups-card">
          {/* <div className="ups-row" onClick={() => navigate('/help-center')}>
            <span className="ups-row-icon"><LifeBuoy size={16} /></span>
            <span className="ups-row-label">Help Center</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/faq')}>
            <span className="ups-row-icon"><HelpCircle size={16} /></span>
            <span className="ups-row-label">FAQs</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div> */}
          <div className="ups-row" onClick={() => navigate('/terms-of-use')}>
            <span className="ups-row-icon"><ShieldCheck size={16} /></span>
            <span className="ups-row-label">Terms of Use</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/privacy-policy')}>
            <span className="ups-row-icon"><ShieldCheck size={16} /></span>
            <span className="ups-row-label">Privacy Policy</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
          <div className="ups-row" onClick={() => navigate('/refund-policy')}>
            <span className="ups-row-icon"><ShieldCheck size={16} /></span>
            <span className="ups-row-label">Refund Policy</span>
            <ChevronRight size={16} className="ups-row-arrow" />
          </div>
        </div>

        <CurrentOrdersList />

        {/* ============ LOGOUT / DELETE ACCOUNT ============ */}
        <button type="button" className="ups-logout-btn" onClick={handleLogout}>
          Logout
        </button>
        <button type="button" className="ups-delete-btn" onClick={handleDeleteAccount}>
          Delete Account
        </button>

        <p className="ups-version">ClaimPro Assist</p>
      </div>

      <BottomNavigationCustomer />
      <ToastContainer />
      {/* ============ DELETE ACCOUNT CONFIRM MODAL ============ */}
      {showDeleteConfirm && (
        <div className="ups-modal-backdrop" onClick={() => setShowDeleteConfirm(false)}>
          <div className="ups-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="ups-modal-icon">
              <ShieldCheck size={22} />
            </div>
            <h3 className="ups-modal-title">Delete your account?</h3>
            <p className="ups-modal-body">
              This will permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <div className="ups-modal-actions">
              <button
                type="button"
                className="ups-modal-btn ups-modal-btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ups-modal-btn ups-modal-btn-danger"
                onClick={confirmDeleteAccount}
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ============ LOGOUT CONFIRM MODAL ============ */}
      {showLogoutConfirm && (
        <div className="ups-modal-backdrop" onClick={() => setShowLogoutConfirm(false)}>
          <div className="ups-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="ups-modal-icon ups-modal-icon-blue">
              <LifeBuoy size={22} />
            </div>
            <h3 className="ups-modal-title">Log out?</h3>
            <p className="ups-modal-body">
              You'll need to log in again to access your account.
            </p>
            <div className="ups-modal-actions">
              <button
                type="button"
                className="ups-modal-btn ups-modal-btn-ghost"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ups-modal-btn ups-modal-btn-danger"
                onClick={confirmLogout}
              >
                Yes, logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileSection;